// src/pages/services-overview/components/ServicesShowcase.jsx
import React, { useMemo, useRef, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { servicesData } from "../servicesData";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const ORANGE = "#F27E33";
const NAVY   = "#1D2946";
const CYAN   = "#00D4FF"; // Se mantiene la constante CYAN

const getHeaderHeight = () => {
  const header =
    document.querySelector("header") || document.getElementById("site-header");
  return header ? header.getBoundingClientRect().height : 88;
};

export default function ServicesShowcase() {
  const location = useLocation();
  const hashSlug = location.hash?.replace("#", "");
  const defaultSlug = "obra-civil";

  const initial = useMemo(() => {
    const exists = servicesData.some((s) => s.slug === hashSlug);
    return exists ? hashSlug : defaultSlug;
  }, [hashSlug]);

  const [active, setActive] = useState(initial);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!contentRef.current) return;
    const headerH = getHeaderHeight();
    const extra = 16;
    const y =
      contentRef.current.getBoundingClientRect().top +
      window.scrollY -
      headerH -
      extra;

    window.scrollTo({ top: y, behavior: "smooth" });
  }, [active]);

  const activeItem =
    servicesData.find((s) => s.slug === active) || servicesData[0];

  const railItemClass = (slug) =>
    `w-full text-left rounded-xl border px-3 py-2 transition ${
      slug === active
        ? "border-white/30 bg-white/15 text-white"
        : "border-white/15 bg-white/10 text-white/85 hover:text-white hover:bg-white/12"
    }`;

  return (
    <div className="relative lg:grid lg:grid-cols-12 lg:gap-8">
      {/* Rail */}
      <aside className="hidden lg:block lg:col-span-3">
        <div className="sticky top-28">
          <nav className="space-y-2" aria-label="Servicios">
            {servicesData.map((s) => (
              <button
                key={s.slug}
                onClick={() => setActive(s.slug)}
                aria-current={active === s.slug ? "true" : "false"}
                className={railItemClass(s.slug)}
              >
                <span className="flex items-center gap-2">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: active === s.slug ? ORANGE : "transparent" }}
                  />
                  {s.name}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </aside>

      {/* Contenido */}
      <div ref={contentRef} className="lg:col-span-9 min-w-0">
        <AnimatePresence mode="wait">
          <motion.section
            key={activeItem.slug}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.35 }}
            className="scroll-mt-28 mt-10"
          >
            <div className="relative rounded-2xl border border-white/15 bg-white/10 backdrop-blur-sm p-6">
              <div
                className="pointer-events-none absolute inset-0 rounded-2xl"
                style={{
                  boxShadow: `0 0 0 1px #ffffff1A, inset 0 0 60px ${ORANGE}22`,
                }}
              />
              <div
                className="absolute -top-[1px] left-0 h-1 w-40 rounded-tr"
                style={{
                  background: `linear-gradient(90deg, ${ORANGE}, ${NAVY})`,
                }}
              />

              <div className="relative z-10">
                <h3 className="font-orbitron text-white text-2xl md:text-3xl font-semibold">
                  {activeItem.name}
                </h3>

                {activeItem.description && (
                  <p className="text-white/85 mt-3">{activeItem.description}</p>
                )}

                {!!activeItem.bullets?.length && (
                  <ul className="mt-4 list-disc pl-5 space-y-1 text-white/85">
                    {activeItem.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                )}
                
                {/* ===== SECCIÓN MODIFICADA AQUÍ ===== */}
                <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                  {/* Botón CTA Principal Retocado - SOLO ORANGE y NAVY */}
                  <Link to="/contact-quote-request" aria-label={`Cotizar ahora ${activeItem.name}`}>
                    <Button
                        variant="default"
                        size="lg"
                        className="bg-gradient-to-r from-secondary to-primary hover:from-secondary/90 hover:to-primary/90 glow-effect font-orbitron w-full sm:w-auto"
                        iconName="ArrowRight"
                        iconPosition="right"
                    >
                        Cotizar Ahora
                    </Button>
                  </Link>
                  
                  {/* Badges Informativos (CON CYAN) */}
                  <div className="flex items-center gap-x-6 gap-y-3 text-sm flex-wrap justify-center sm:justify-end">
                    <div className="flex items-center gap-2 text-white/85">
                      <Icon name="Award" size={20} style={{ color: CYAN }} />
                      <span>Calidad Certificada</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/85">
                      <Icon name="Clock" size={20} style={{ color: CYAN }} />
                      <span>Atención 24/7</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/85">
                      <Icon name="Wrench" size={20} style={{ color: CYAN }} />
                      <span>Soporte Post-Entrega</span>
                    </div>
                  </div>
                </div>
                {/* ===== FIN DE LA SECCIÓN MODIFICADA ===== */}
              </div>
            </div>
          </motion.section>
        </AnimatePresence>

        {/* Navegación */}
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={() => {
              const idx = servicesData.findIndex((s) => s.slug === active);
              const prev =
                servicesData[idx - 1] || servicesData[servicesData.length - 1];
              setActive(prev.slug);
            }}
            className="rounded-lg border border-white/15 bg-white/10 px-3 py-2 text-white/85 hover:bg-white/12 transition"
          >
            ← Anterior
          </button>
          <button
            onClick={() => {
              const idx = servicesData.findIndex((s) => s.slug === active);
              const next = servicesData[idx + 1] || servicesData[0];
              setActive(next.slug);
            }}
            className="rounded-lg border border-white/15 bg-white/10 px-3 py-2 text-white/85 hover:bg-white/12 transition"
          >
            Siguiente →
          </button>
        </div>
      </div>
    </div>
  );
}